/// <reference lib="webworker" />

import { initializeApp } from 'firebase/app';
import { getMessaging, onBackgroundMessage } from 'firebase/messaging/sw';
import { handleFcmPayload } from './handleFcmPayload';
import { convertBase64UrlToUint8Array } from '../lib/encoding';
import { captureException } from '../helper/sentry';
import * as qinit from '../qinit';

// This file runs in Service Worker context where `self` is ServiceWorkerGlobalScope
declare const self: ServiceWorkerGlobalScope;

/**
 * Ensure push subscription with userVisibleOnly: false
 *
 * Prevents Chrome from showing "site has been updated in the background" notifications.
 * Executes during Service Worker activation to ensure registration is ready.
 */
const ensurePushSubscription = async (): Promise<void> => {
  try {
    const registration = self.registration;
    const existing = await registration.pushManager.getSubscription();

    if (existing) {
      return;
    }

    const vapidKey =
      qinit.tenant.chromebook.pushNotificationConfig?.vapidKey?.key;
    if (!vapidKey) {
      return;
    }

    const applicationServerKey = convertBase64UrlToUint8Array(vapidKey);

    await registration.pushManager.subscribe({
      userVisibleOnly: false,
      applicationServerKey,
    });
  } catch (error) {
    console.error('[Push] Error ensuring push subscription:', error);
    // Report to Sentry as this affects push notification functionality
    captureException(new Error('Failed to ensure push subscription'), {
      extra: { originalError: error },
    });
  }
};

/**
 * Firebase Messaging setup (Service Worker only)
 *
 * Initializes Firebase and synchronously registers the `onBackgroundMessage`
 * listener for push notifications.
 *
 * **IMPORTANT**: This function must be called synchronously during the initial
 * Service Worker script evaluation, before any async code. Chrome requires
 * push event listeners to be registered synchronously, otherwise FCM messages
 * will not be received.
 */
export const setupFirebaseMessaging = (): void => {
  const firebaseConfig =
    qinit.tenant.chromebook.pushNotificationConfig?.firebase;
  const firebase = firebaseConfig ? initializeApp(firebaseConfig) : null;

  if (firebase) {
    const messaging = getMessaging(firebase);
    onBackgroundMessage(messaging, handleFcmPayload);
  }

  // Ensure push subscription when Service Worker is activated
  self.addEventListener('activate', (event: ExtendableEvent) => {
    event.waitUntil(ensurePushSubscription());
  });
};
